import React, { useState, useEffect, forwardRef } from "react";
import Modal from "react-bootstrap/Modal";
import Select from "react-select";
import citiesData from "./misc/cities.json";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const statuses = [
  { value: "Under review", label: "Under review", color: "#d97706" },
  { value: "Escalated", label: "Escalated", color: "#dc2626" },
  { value: "Awaiting feedback", label: "Awaiting feedback", color: "#ca8a04" },
  { value: "Resolved", label: "Resolved", color: "#16a34a" },
];

const priorities = [
  { value: "High", label: "High", color: "#dc2626" },
  { value: "Medium", label: "Medium", color: "#d97706" },
  { value: "Low", label: "Low", color: "#0d9488" },
  { value: "Spam", label: "Spam", color: "#9ca3af" },
];

const categoryOptions = [
  { value: "Agriculture", label: "Agriculture" },
  { value: "Apparel", label: "Apparel" },
  { value: "Automobiles", label: "Automobiles" },
  { value: "Beverages", label: "Beverages" },
  { value: "Construction", label: "Construction" },
  { value: "Cosmetics", label: "Cosmetics" },
  { value: "Currencies", label: "Currencies" },
  { value: "Education", label: "Education" },
  { value: "Electronics", label: "Electronics" },
  { value: "Food", label: "Food" },
  { value: "Government", label: "Government" },
  { value: "Media", label: "Media" },
  { value: "Organizations", label: "Organizations" },
  { value: "Petroleum", label: "Petroleum" },
  { value: "Pharmaceuticals", label: "Pharmaceuticals" },
];

const makeSelectStyles = (coloredValue = false) => ({
  control: (s, { isFocused }) => ({
    ...s,
    backgroundColor: "#fff",
    border: `1.5px solid ${isFocused ? "#ff5100" : "#e5e7eb"}`,
    borderRadius: 10,
    padding: "2px 4px",
    boxShadow: isFocused ? "0 0 0 3px rgba(255,81,0,0.1)" : "none",
    "&:hover": { borderColor: "#ff5100" },
  }),
  singleValue: (s, { data }) => ({
    ...s,
    color: coloredValue ? (data.color || "#111827") : "#111827",
    fontWeight: 500,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (s) => ({ ...s, color: "#9ca3af" }),
  clearIndicator: (s) => ({ ...s, color: "#9ca3af", "&:hover": { color: "#374151" } }),
  menu: (s) => ({
    ...s,
    backgroundColor: "#fff",
    border: "1.5px solid #e5e7eb",
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
    overflow: "hidden",
    zIndex: 9999,
  }),
  menuPortal: (s) => ({ ...s, zIndex: 9999 }),
  option: (s, { data, isFocused, isSelected }) => ({
    ...s,
    backgroundColor: isSelected
      ? "rgba(255,81,0,0.08)"
      : isFocused
      ? "#f9fafb"
      : "transparent",
    color: coloredValue
      ? (data.color || "#111827")
      : isSelected
      ? "#ff5100"
      : "#374151",
    fontWeight: isSelected ? 600 : 400,
    display: "flex",
    alignItems: "center",
  }),
  input: (s) => ({ ...s, color: "#111827" }),
  placeholder: (s) => ({ ...s, color: "#9ca3af" }),
  multiValue: (s) => ({
    ...s,
    backgroundColor: "rgba(255,81,0,0.1)",
    borderRadius: 6,
  }),
  multiValueLabel: (s) => ({ ...s, color: "#c2410c", fontWeight: 600 }),
  multiValueRemove: (s) => ({
    ...s,
    color: "#c2410c",
    "&:hover": { backgroundColor: "rgba(255,81,0,0.2)", color: "#9a3412" },
  }),
});

const ColorDot = ({ color }) => (
  <span
    style={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: color,
      display: "inline-block",
      marginRight: 8,
      flexShrink: 0,
    }}
  />
);

const FieldLabel = ({ children }) => (
  <div
    style={{
      color: "#6b7280",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 6,
    }}
  >
    {children}
  </div>
);

const CRMFilterModal = ({ domain, show, handleClose, handleFormSubmit }) => {
  const cityOptions = citiesData.Uganda.map((city) => ({ value: city, label: city }));

  const [tags, setTags] = useState([]);
  const [status, setStatus] = useState(null);
  const [category, setCategory] = useState(null);
  const [city, setCity] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [priority, setPriority] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (!show) return;
    fetch("/api/tickets/tags")
      .then((res) => res.json())
      .then((data) => setTags(data))
      .catch((err) => console.error("Error fetching tags:", err));
  }, [show]);

  const handleApply = () => {
    if (handleFormSubmit) {
      handleFormSubmit({
        status: status?.value || "",
        category: category?.value || "",
        city: city?.value || "",
        tags: selectedTags.map((t) => t.value),
        priority: priority?.value || "",
        startDate,
        endDate,
      });
    }
  };

  const handleClear = () => {
    setStatus(null);
    setCategory(null);
    setCity(null);
    setSelectedTags([]);
    setPriority(null);
    setStartDate(null);
    setEndDate(null);
  };

  const InlineDatePicker = forwardRef(({ value, onClick }, ref) => (
    <button
      onClick={onClick}
      ref={ref}
      style={{
        width: "100%",
        background: "transparent",
        border: "none",
        padding: "2px 0",
        fontSize: 14,
        fontWeight: value ? 600 : 400,
        color: value ? "#111827" : "#9ca3af",
        textAlign: "left",
        cursor: "pointer",
        outline: "none",
      }}
    >
      {value || "Pick a date"}
    </button>
  ));

  const coloredStyles = makeSelectStyles(true);
  const plainStyles = makeSelectStyles(false);
  const fieldGap = { marginTop: 16 };

  const activeFilters = [status, category, city, priority, startDate, endDate].filter(Boolean).length
    + selectedTags.length;

  return (
    <>
      <style>{`
        .crm-filter-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .crm-filter-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .crm-filter-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .crm-filter-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
          gap: 10px;
        }
        .react-datepicker {
          font-family: inherit !important;
          border: 1.5px solid #e5e7eb !important;
          border-radius: 12px !important;
          box-shadow: 0 8px 24px rgba(0,0,0,0.1) !important;
          overflow: hidden;
        }
        .react-datepicker__header {
          background: #f9fafb !important;
          border-bottom: 1px solid #f3f4f6 !important;
        }
        .react-datepicker__current-month,
        .react-datepicker__day-name {
          color: #374151 !important;
          font-weight: 600 !important;
        }
        .react-datepicker__day {
          color: #374151 !important;
          border-radius: 6px !important;
        }
        .react-datepicker__day:hover {
          background: rgba(255,81,0,0.1) !important;
          color: #ff5100 !important;
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--in-range,
        .react-datepicker__day--in-selecting-range {
          background: rgba(255,81,0,0.15) !important;
          color: #c2410c !important;
        }
        .react-datepicker__day--range-start,
        .react-datepicker__day--range-end,
        .react-datepicker__day--keyboard-selected {
          background: #ff5100 !important;
          color: #fff !important;
          font-weight: 700 !important;
        }
        .react-datepicker__day--outside-month {
          color: #d1d5db !important;
        }
        .react-datepicker__navigation-icon::before {
          border-color: #9ca3af !important;
        }
        .react-datepicker__triangle::before,
        .react-datepicker__triangle::after {
          display: none !important;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="crm-filter-modal"
        centered
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i className="fas fa-sliders-h" style={{ color: "#ff5100", fontSize: 14 }} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Filters</span>
                {activeFilters > 0 && (
                  <span
                    style={{
                      background: "#ff5100",
                      color: "#fff",
                      fontSize: 11,
                      fontWeight: 700,
                      borderRadius: 999,
                      padding: "1px 7px",
                    }}
                  >
                    {activeFilters}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                Narrow down your results
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div>
            <FieldLabel>Status</FieldLabel>
            <Select
              options={statuses}
              value={status}
              onChange={setStatus}
              isClearable
              placeholder="Any status"
              menuPortalTarget={document.body}
              styles={coloredStyles}
              getOptionLabel={(e) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <ColorDot color={e.color} />
                  {e.label}
                </div>
              )}
            />
          </div>

          <div style={fieldGap}>
            <FieldLabel>Priority</FieldLabel>
            <Select
              options={priorities}
              value={priority}
              onChange={setPriority}
              isClearable
              placeholder="Any priority"
              menuPortalTarget={document.body}
              styles={coloredStyles}
              getOptionLabel={(e) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <ColorDot color={e.color} />
                  {e.label}
                </div>
              )}
            />
          </div>

          <div style={fieldGap}>
            <FieldLabel>Category</FieldLabel>
            <Select
              options={categoryOptions}
              value={category}
              onChange={setCategory}
              isClearable
              placeholder="Any category"
              menuPortalTarget={document.body}
              styles={plainStyles}
            />
          </div>

          <div style={fieldGap}>
            <FieldLabel>City</FieldLabel>
            <Select
              options={cityOptions}
              value={city}
              onChange={setCity}
              isClearable
              placeholder="Any city"
              menuPortalTarget={document.body}
              styles={plainStyles}
            />
          </div>

          <div style={fieldGap}>
            <FieldLabel>Tags</FieldLabel>
            <Select
              isMulti
              options={tags.map((tag) => ({ value: tag, label: tag }))}
              value={selectedTags}
              onChange={setSelectedTags}
              isClearable={false}
              placeholder="Any tags"
              menuPortalTarget={document.body}
              styles={plainStyles}
            />
          </div>

          <div style={fieldGap}>
            <FieldLabel>Date range</FieldLabel>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1.5px solid #e5e7eb",
                borderRadius: 10,
                overflow: "hidden",
                transition: "border-color 0.15s",
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "#ff5100")}
              onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
            >
              <div style={{ flex: 1, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.05em", marginBottom: 3 }}>FROM</div>
                <DatePicker
                  selected={startDate}
                  onChange={setStartDate}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  customInput={<InlineDatePicker />}
                  dateFormat="MMM d, yyyy"
                  placeholderText="Pick a date"
                />
              </div>
              <div style={{ width: 1, alignSelf: "stretch", background: "#e5e7eb" }} />
              <div style={{ flex: 1, padding: "10px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.05em", marginBottom: 3 }}>TO</div>
                <DatePicker
                  selected={endDate}
                  onChange={setEndDate}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  customInput={<InlineDatePicker />}
                  dateFormat="MMM d, yyyy"
                  placeholderText="Pick a date"
                />
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={handleClear}
            style={{
              flex: 1,
              padding: "10px 0",
              borderRadius: 10,
              border: "1.5px solid #e5e7eb",
              background: "#fff",
              color: "#6b7280",
              fontWeight: 600,
              fontSize: 14,
              cursor: "pointer",
              transition: "border-color 0.15s, color 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#d1d5db";
              e.currentTarget.style.color = "#374151";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={handleApply}
            style={{
              flex: 2,
              padding: "10px 0",
              borderRadius: 10,
              border: "none",
              background: "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            Apply filters
            <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CRMFilterModal;
